import { create } from "zustand";
import { devtools } from "zustand/middleware";
import type {
    HiddenCols,
    TableHeader,
} from "../components/ReusableComponents/Table";
import type { FilterType } from "../models/app/admin/filter"; // required for devtools typing
import { driverListFiltersDefaultValues } from "../components/DriverListFilters/DriversListFiltersForm/driversListFiltersDefaultValues";
import {
    driverCategoryOptionsEn,
    type DriverCategoryOptionsType,
} from "../utils/driverPageLocation";
import { match } from "ts-pattern";
import {
    tableHeaderOptionsEnum,
    type TableHeaderOptionsType,
} from "../hooks/useTableHeaders";

type TableStoreState = {
    // Table Headers
    tableHeaders: TableHeader[];
    tableHeadersGreyFleet: TableHeader[];
    tableHeadersDeclarations: TableHeader[];

    // Hidden Columns
    hiddenColsAllDrivers: HiddenCols[];
    hiddenColsProfessionalDrivers: HiddenCols[];
    hiddenColsGreyFleet: HiddenCols[];
    hiddenColsDeclarations: HiddenCols[];

    // Table UI State
    scrollToTable: boolean;
    isRedirect: boolean;

    // Filters
    appliedFilterAllDrivers: FilterType;
    appliedFilterProfessionalDrivers: FilterType;
    appliedFilterGreyFleet: FilterType;

    // OrderBy/ SortOrder/ PageNumber/ PageSize
    allDriversOrderBy: string;
    allDriversSortOrder: string;
    allDriversPageNumber: number;
    allDriversPageSize: number;

    professionalDriversOrderBy: string;
    professionalDriversSortOrder: string;
    professionalDriversPageNumber: number;
    professionalDriversPageSize: number;

    greyFleetOrderBy: string;
    greyFleetSortOrder: string;
    greyFleetPageNumber: number;
    greyFleetPageSize: number;

    declarationsOrderBy: string;
    declarationsSortOrder: "asc" | "desc";
    declarationsPageNumber: number;
    declarationsPageSize: number;

    dvlaChecksSortOrder: "asc" | "desc";
    dvlaChecksOrderBy: string;
    dvlaChecksPageNumber: number;
    dvlaChecksPageSize: number;

    // Table Headers Methods
    setTableHeaders: (
        headers: TableHeader[],
        option: TableHeaderOptionsType
    ) => void;
    getTableHeaders: (option: TableHeaderOptionsType) => TableHeader[];

    // Hidden Columns Methods
    setHiddenCols: (
        hiddenCols: HiddenCols[],
        option: TableHeaderOptionsType
    ) => void;
    getHiddenCols: (option: TableHeaderOptionsType) => HiddenCols[];

    // Table UI Methods
    setScrollToTable: (scrollToTable: boolean) => void;
    setIsRedirect: (isRedirect: boolean) => void;

    // Filter Methods
    setAppliedFilter: (
        filter: FilterType,
        location: DriverCategoryOptionsType
    ) => void;
    getAppliedFilter: (location: DriverCategoryOptionsType) => FilterType;

    // OrderBy/ SortOrder/ PageNumber/ PageSize Methods
    setAllDriversOrderBy: (orderBy: string) => void;
    setAllDriversSortOrder: (orderSort: string) => void;
    setAllDriversPageNumber: (pageNumber: number) => void;
    setAllDriversPageSize: (pageSize: number) => void;

    setProfessionalDriversOrderBy: (orderBy: string) => void;
    setProfessionalDriversSortOrder: (orderSort: string) => void;
    setProfessionalDriversPageNumber: (pageNumber: number) => void;
    setProfessionalDriversPageSize: (pageSize: number) => void;

    setGreyFleetOrderBy: (orderBy: string) => void;
    setGreyFleetSortOrder: (orderSort: string) => void;
    setGreyFleetPageNumber: (pageNumber: number) => void;
    setGreyFleetPageSize: (pageSize: number) => void;

    setDeclarationsOrderBy: (orderBy: string) => void;
    setDeclarationsSortOrder: (orderSort: "asc" | "desc") => void;
    setDeclarationsPageNumber: (pageNumber: number) => void;
    setDeclarationsPageSize: (pageSize: number) => void;

    setDvlaChecksSortOrder: (orderSort: "asc" | "desc") => void;
    setDvlaChecksOrderBy: (orderBy: string) => void;
    setDvlaChecksPageNumber: (pageNumber: number) => void;
    setDvlaChecksPageSize: (pageSize: number) => void;

    // Reset Method
    resetTableStore: () => void;
};

const defaultState = {
    // Table Headers
    tableHeaders: [],
    tableHeadersGreyFleet: [],
    tableHeadersDeclarations: [],

    // Hidden Columns
    hiddenColsAllDrivers: [],
    hiddenColsProfessionalDrivers: [],
    hiddenColsGreyFleet: [],
    hiddenColsDeclarations: [],

    // Table UI State
    /** scrollToTable set to true when applying a filter via link */
    scrollToTable: false,
    /** isRedirect set to true when navigating via Dashboard link
     *  so we do not set table config or filters twice */
    isRedirect: false,

    // Filters
    appliedFilterAllDrivers: {
        filterId: "",
        filterFields: {},
    },
    appliedFilterProfessionalDrivers: {
        filterId: "",
        filterFields: driverListFiltersDefaultValues(
            driverCategoryOptionsEn.professionalDrivers
        ),
    },
    appliedFilterGreyFleet: {
        filterId: "",
        filterFields: driverListFiltersDefaultValues(
            driverCategoryOptionsEn.greyfleet
        ),
    },

    // OrderBy/ SortOrder/ PageNumber/ PageSize
    allDriversOrderBy: "LastName",
    allDriversSortOrder: "asc",
    allDriversPageNumber: 1,
    allDriversPageSize: 25,

    professionalDriversOrderBy: "LastName",
    professionalDriversSortOrder: "asc",
    professionalDriversPageNumber: 1,
    professionalDriversPageSize: 25,

    greyFleetOrderBy: "LastName",
    greyFleetSortOrder: "asc",
    greyFleetPageNumber: 1,
    greyFleetPageSize: 25,

    declarationsOrderBy: "LastName",
    declarationsSortOrder: "asc" as "asc" | "desc",
    declarationsPageNumber: 1,
    declarationsPageSize: 25,

    dvlaChecksOrderBy: "LastName",
    dvlaChecksSortOrder: "asc" as "asc" | "desc",
    dvlaChecksPageNumber: 1,
    dvlaChecksPageSize: 25,
};

export const createActionName = (partial: Partial<TableStoreState>): string => {
    const keys = Object.keys(partial);
    if (keys.length === 1) {
        return `set${keys[0].charAt(0).toUpperCase()}${keys[0].slice(1)}`;
    }
    return `setBulk(${keys.join(",")})`;
};

const useTableStore = create<TableStoreState>()(
    devtools((set, get) => {
        const setWithAction = (
            partial: Partial<TableStoreState>,
            customAction?: string
        ) => set(partial, false, customAction ?? createActionName(partial));

        return {
            ...defaultState,

            // Table Headers Methods
            setTableHeaders: (headers, option) => {
                match(option)
                    .with(
                        tableHeaderOptionsEnum.allDrivers,
                        tableHeaderOptionsEnum.professionalDrivers,
                        () =>
                            setWithAction(
                                { tableHeaders: headers },
                                "setTableHeaders/allAndProfessional"
                            )
                    )
                    .with(tableHeaderOptionsEnum.greyFleet, () =>
                        setWithAction(
                            { tableHeadersGreyFleet: headers },
                            "setTableHeaders/greyFleet"
                        )
                    )
                    .with(tableHeaderOptionsEnum.declarations, () =>
                        setWithAction(
                            { tableHeadersDeclarations: headers },
                            "setTableHeaders/declarations"
                        )
                    )
                    .otherwise(() =>
                        setWithAction(
                            { tableHeaders: headers },
                            "setTableHeaders/default"
                        )
                    );
            },
            getTableHeaders: (option) => {
                return match(option)
                    .with(
                        tableHeaderOptionsEnum.allDrivers,
                        tableHeaderOptionsEnum.professionalDrivers,
                        () => get().tableHeaders
                    )
                    .with(
                        tableHeaderOptionsEnum.greyFleet,
                        () => get().tableHeadersGreyFleet
                    )
                    .with(
                        tableHeaderOptionsEnum.declarations,
                        () => get().tableHeadersDeclarations
                    )
                    .otherwise(() => get().tableHeaders);
            },

            // Hidden Columns Methods
            setHiddenCols: (hiddenCols, option) => {
                match(option)
                    .with(tableHeaderOptionsEnum.allDrivers, () =>
                        setWithAction({ hiddenColsAllDrivers: hiddenCols })
                    )
                    .with(tableHeaderOptionsEnum.professionalDrivers, () =>
                        setWithAction({
                            hiddenColsProfessionalDrivers: hiddenCols,
                        })
                    )
                    .with(tableHeaderOptionsEnum.greyFleet, () =>
                        setWithAction({ hiddenColsGreyFleet: hiddenCols })
                    )
                    .with(tableHeaderOptionsEnum.declarations, () =>
                        setWithAction({
                            hiddenColsDeclarations: hiddenCols,
                        })
                    )
                    .otherwise(() => {
                        setWithAction({ hiddenColsAllDrivers: hiddenCols });
                    });
            },
            getHiddenCols: (option) => {
                return match(option)
                    .with(
                        tableHeaderOptionsEnum.allDrivers,
                        () => get().hiddenColsAllDrivers
                    )
                    .with(
                        tableHeaderOptionsEnum.professionalDrivers,
                        () => get().hiddenColsProfessionalDrivers
                    )
                    .with(
                        tableHeaderOptionsEnum.greyFleet,
                        () => get().hiddenColsGreyFleet
                    )
                    .with(
                        tableHeaderOptionsEnum.declarations,
                        () => get().hiddenColsDeclarations
                    )
                    .otherwise(() => get().hiddenColsAllDrivers);
            },

            // Table UI Methods
            setScrollToTable: (scrollToTable) =>
                setWithAction({ scrollToTable }),
            setIsRedirect: (isRedirect) => setWithAction({ isRedirect }),

            // Filter Methods
            setAppliedFilter: (filter, location) => {
                match(location)
                    .with(driverCategoryOptionsEn.drivers, () =>
                        setWithAction({ appliedFilterAllDrivers: filter })
                    )
                    .with(driverCategoryOptionsEn.professionalDrivers, () =>
                        setWithAction({
                            appliedFilterProfessionalDrivers: filter,
                        })
                    )
                    .with(driverCategoryOptionsEn.greyfleet, () =>
                        setWithAction({ appliedFilterGreyFleet: filter })
                    )
                    .otherwise(() =>
                        setWithAction({ appliedFilterAllDrivers: filter })
                    );
            },
            getAppliedFilter: (location) => {
                return match(location)
                    .with(
                        driverCategoryOptionsEn.drivers,
                        () => get().appliedFilterAllDrivers
                    )
                    .with(
                        driverCategoryOptionsEn.professionalDrivers,
                        () => get().appliedFilterProfessionalDrivers
                    )
                    .with(
                        driverCategoryOptionsEn.greyfleet,
                        () => get().appliedFilterGreyFleet
                    )
                    .otherwise(() => get().appliedFilterAllDrivers);
            },

            // OrderBy/SortOrder/PageNumber/PageSize Methods
            setAllDriversOrderBy: (orderBy) =>
                setWithAction({ allDriversOrderBy: orderBy }),
            setAllDriversSortOrder: (orderSort) =>
                setWithAction({ allDriversSortOrder: orderSort }),
            setAllDriversPageNumber: (pageNumber) =>
                setWithAction({ allDriversPageNumber: pageNumber }),
            setAllDriversPageSize: (pageSize) =>
                setWithAction({ allDriversPageSize: pageSize }),

            setProfessionalDriversOrderBy: (orderBy) =>
                setWithAction({ professionalDriversOrderBy: orderBy }),
            setProfessionalDriversSortOrder: (orderSort) =>
                setWithAction({ professionalDriversSortOrder: orderSort }),
            setProfessionalDriversPageNumber: (pageNumber) =>
                setWithAction({
                    professionalDriversPageNumber: pageNumber,
                }),
            setProfessionalDriversPageSize: (pageSize) =>
                setWithAction({ professionalDriversPageSize: pageSize }),

            setGreyFleetOrderBy: (orderBy) =>
                setWithAction({ greyFleetOrderBy: orderBy }),
            setGreyFleetSortOrder: (orderSort) =>
                setWithAction({ greyFleetSortOrder: orderSort }),
            setGreyFleetPageNumber: (pageNumber) =>
                setWithAction({ greyFleetPageNumber: pageNumber }),
            setGreyFleetPageSize: (pageSize) =>
                setWithAction({ greyFleetPageSize: pageSize }),

            setDeclarationsOrderBy: (orderBy) =>
                setWithAction({ declarationsOrderBy: orderBy }),
            setDeclarationsSortOrder: (orderSort) =>
                setWithAction({ declarationsSortOrder: orderSort }),
            setDeclarationsPageNumber: (pageNumber) =>
                setWithAction({ declarationsPageNumber: pageNumber }),
            setDeclarationsPageSize: (pageSize) =>
                setWithAction({ declarationsPageSize: pageSize }),

            setDvlaChecksSortOrder: (orderSort) =>
                setWithAction({ dvlaChecksSortOrder: orderSort }),
            setDvlaChecksOrderBy: (orderBy) =>
                setWithAction({ dvlaChecksOrderBy: orderBy }),
            setDvlaChecksPageNumber: (pageNumber) =>
                setWithAction({ dvlaChecksPageNumber: pageNumber }),
            setDvlaChecksPageSize: (pageSize) =>
                setWithAction({ dvlaChecksPageSize: pageSize }),

            // Reset Method
            resetTableStore: () => {
                setWithAction(defaultState, "resetTableStore");
            },
        };
    })
);

export default useTableStore;
